import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';

const newId = () => (window.crypto?.randomUUID?.() || `mcp-${Date.now()}-${Math.random().toString(16).slice(2)}`);
function McpServersEditor({ servers, onChange, onDiscover, onDelete, canDiscover, disabled }) {
    const { t } = useTranslation();
    const items = useMemo(() => (Array.isArray(servers) ? servers : []), [servers]);
    const [selectedId, setSelectedId] = useState('');
    const [addDialog, setAddDialog] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [statuses, setStatuses] = useState({});
    const [testingId, setTestingId] = useState('');

    useEffect(() => {
        if (!items.length) setSelectedId('');
        else if (!items.some((server) => server.id === selectedId)) setSelectedId(items[0].id);
    }, [items, selectedId]);

    const selected = useMemo(
        () => items.find((server) => server.id === selectedId) || null,
        [items, selectedId],
    );

    const updateServer = (serverId, updates) => {
        onChange(items.map((server) => (server.id === serverId ? { ...server, ...updates } : server)));
    };

    const updateConnectionSettings = (serverId, updates) => {
        updateServer(serverId, { ...updates, detectedTransport: null, discoveredTools: [], allowedTools: [] });
        setStatuses((current) => ({ ...current, [serverId]: 'untested' }));
    };

    const updateHeader = (server, index, field, value) => {
        const names = [...(server.headerNames || [])];
        const oldName = names[index] || '';
        const headers = { ...(server.headers || {}) };
        if (field === 'name') {
            names[index] = value;
            if (oldName !== value) {
                const oldValue = headers[oldName];
                delete headers[oldName];
                if (oldValue) headers[value] = oldValue;
            }
        } else {
            headers[oldName] = value;
        }
        updateConnectionSettings(server.id, { headerNames: names, headers });
    };

    const removeHeader = (server, index) => {
        const names = [...(server.headerNames || [])];
        const headers = { ...(server.headers || {}) };
        delete headers[names[index]];
        names.splice(index, 1);
        updateConnectionSettings(server.id, { headerNames: names, headers });
    };

    const addServer = () => {
        const name = String(addDialog?.name || '').trim();
        const url = String(addDialog?.url || '').trim();
        if (!name || !url) return;
        const id = newId();
        onChange([...items, {
            id,
            name,
            url,
            transport: 'auto',
            authType: 'none',
            enabled: true,
            allowedTools: [],
            discoveredTools: [],
        }]);
        setSelectedId(id);
        setAddDialog(null);
    };

    const testServer = async (server) => {
        try {
            setTestingId(server.id);
            setStatuses((current) => ({ ...current, [server.id]: 'connecting' }));
            const result = await onDiscover(server.id, items);
            const tools = Array.isArray(result) ? result : (Array.isArray(result?.tools) ? result.tools : []);
            const detectedTransport = Array.isArray(result) ? null : result?.detectedTransport;
            updateServer(server.id, {
                discoveredTools: tools,
                detectedTransport: ['http', 'sse'].includes(detectedTransport) ? detectedTransport : null,
            });
            setStatuses((current) => ({ ...current, [server.id]: 'connected' }));
        } catch (error) {
            console.error('MCP discovery failed', error);
            setStatuses((current) => ({ ...current, [server.id]: 'failed' }));
        } finally {
            setTestingId('');
        }
    };

    const status = (server) => statuses[server.id] || (server.discoveredTools?.length ? 'connected' : 'untested');
    const statusChip = (server) => {
        const variants = {
            connected: { label: t('mcp.connected', 'Connected'), color: 'success' },
            connecting: { label: t('mcp.connecting', 'Connecting…'), color: 'warning' },
            failed: { label: t('mcp.failed', 'Failed'), color: 'error' },
            untested: { label: t('mcp.notTested', 'Not tested'), color: 'default' },
        };
        const value = variants[status(server)];
        return <Chip size="small" label={value.label} color={value.color} variant={status(server) === 'untested' ? 'outlined' : 'filled'} />;
    };

    const tools = Array.isArray(selected?.discoveredTools) ? selected.discoveredTools : [];
    const allowed = Array.isArray(selected?.allowedTools) ? selected.allowedTools : [];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="h6">{t('mcp.integrationTitle', 'MCP Integration')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('mcp.subtitle', 'Connect external data sources and tools to extend this agent’s capabilities.')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddDialog({ name: '', url: '' })}
                    disabled={disabled}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                    {t('mcp.addServer', 'Add MCP Server')}
                </Button>
            </Box>

            {!items.length ? (
                <Alert severity="info">{t('mcp.empty', 'No MCP servers are attached to this agent.')}</Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {items.length > 1 ? (
                        <TextField
                            select
                            size="small"
                            label={t('mcp.selectServer', 'MCP Server')}
                            value={selectedId}
                            onChange={(event) => setSelectedId(event.target.value)}
                            sx={{ maxWidth: 360 }}
                        >
                            {items.map((server) => (
                                <MenuItem key={server.id} value={server.id}>
                                    {server.name} · {status(server) === 'connected' ? t('mcp.connected', 'Connected') : t('mcp.notTested', 'Not tested')}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : null}
                    {selected ? (
                        <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6">{selected.name}</Typography>
                                    {statusChip(selected)}
                                </Box>
                                <IconButton
                                    color="error"
                                    onClick={() => { setDeleteError(''); setDeleteTarget(selected); }}
                                    disabled={disabled}
                                    aria-label={t('mcp.deleteServer', 'Delete MCP server')}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography fontWeight={700}>{t('mcp.enableMcp', 'Enable MCP')}</Typography>
                                        <Typography variant="caption" color="text.secondary">{t('mcp.enableMcpHelp', 'Allow this agent to use tools from this MCP server.')}</Typography>
                                    </Box>
                                    <Switch checked={selected.enabled !== false} onChange={(event) => updateServer(selected.id, { enabled: event.target.checked })} disabled={disabled} />
                                </Box>

                                <TextField
                                    label={t('mcp.serverName', 'Server name')}
                                    value={selected.name}
                                    onChange={(event) => updateServer(selected.id, { name: event.target.value })}
                                    disabled={disabled}
                                />

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' } }}>
                                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: 1, borderColor: 'primary.main', borderRadius: { xs: '4px 4px 0 0', sm: '4px 0 0 4px' }, bgcolor: 'primary.50', color: 'primary.main', fontWeight: 700 }}>
                                        <span>{selected.detectedTransport === 'http' ? 'Streamable HTTP' : selected.detectedTransport === 'sse' ? 'SSE' : 'SSE / HTTP'}</span>
                                        {selected.detectedTransport ? (
                                            <Typography component="span" variant="caption" color="success.main" fontWeight={700}>
                                                {t('mcp.detected', 'Detected')}
                                            </Typography>
                                        ) : (
                                            <Typography component="span" variant="caption" color="text.secondary" fontWeight={700}>
                                                {t('mcp.autoDetect', 'Auto-detect')}
                                            </Typography>
                                        )}
                                    </Box>
                                    <TextField
                                        label={t('mcp.serverUrl', 'Server URL')}
                                        value={selected.url}
                                        onChange={(event) => updateConnectionSettings(selected.id, { url: event.target.value })}
                                        disabled={disabled}
                                        sx={{ ml: { sm: '-1px' }, mt: { xs: '-1px', sm: 0 }, '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: { sm: 0 } } }}
                                    />
                                </Box>

                                <TextField
                                    select
                                    label={t('mcp.authMethod', 'Authentication method')}
                                    value={selected.authType || 'none'}
                                    onChange={(event) => updateConnectionSettings(selected.id, { authType: event.target.value })}
                                    disabled={disabled}
                                >
                                    <MenuItem value="none">{t('mcp.authNone', 'No authentication')}</MenuItem>
                                    <MenuItem value="bearer">{t('mcp.authBearer', 'Bearer token')}</MenuItem>
                                </TextField>

                                {selected.authType === 'bearer' ? (
                                    <TextField
                                        label={t('mcp.bearerToken', 'Bearer token')}
                                        type="password"
                                        value={selected.bearerToken || ''}
                                        onChange={(event) => updateConnectionSettings(selected.id, { bearerToken: event.target.value })}
                                        disabled={disabled}
                                        autoComplete="new-password"
                                        placeholder={selected.hasBearerToken ? t('mcp.tokenStored', 'A token is already stored') : ''}
                                        helperText={selected.hasBearerToken
                                            ? t('mcp.tokenStoredHelp', 'Leave blank to keep the stored token, or enter a new token to replace it.')
                                            : t('mcp.tokenInputHelp', 'The token is stored securely and will not be shown again.')}
                                    />
                                ) : null}

                                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                        <Box>
                                            <Typography fontWeight={700}>{t('mcp.requestHeaders', 'Request headers')}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('mcp.requestHeadersHelp', 'Add custom headers required by the MCP server. Values are stored securely and are never displayed again.')}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            disabled={disabled || (selected.headerNames || []).length >= 20}
                                            onClick={() => updateConnectionSettings(selected.id, { headerNames: [...(selected.headerNames || []), ''], headers: { ...(selected.headers || {}) } })}
                                        >
                                            {t('mcp.addHeader', 'Add header')}
                                        </Button>
                                    </Box>
                                    {(selected.headerNames || []).map((name, index) => (
                                        <Box key={`${index}-${name}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto' }, gap: 1, mt: 1.25 }}>
                                            <TextField
                                                size="small"
                                                label={t('mcp.headerName', 'Header name')}
                                                value={name}
                                                onChange={(event) => updateHeader(selected, index, 'name', event.target.value)}
                                                disabled={disabled}
                                            />
                                            <TextField
                                                size="small"
                                                label={t('mcp.headerValue', 'Header value')}
                                                type="password"
                                                value={(selected.headers || {})[name] || ''}
                                                onChange={(event) => updateHeader(selected, index, 'value', event.target.value)}
                                                disabled={disabled || !name}
                                                autoComplete="new-password"
                                                placeholder={t('mcp.headerStored', 'Stored value (leave blank to keep)')}
                                                sx={{ gridColumn: { xs: '1 / 2', sm: 'auto' } }}
                                            />
                                            <IconButton aria-label={t('mcp.removeHeader', 'Remove header')} onClick={() => removeHeader(selected, index)} disabled={disabled}>
                                                <RemoveCircleOutlineIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                        <Box>
                                            <Typography fontWeight={700}>{t('mcp.availableTools', 'Available tools')}</Typography>
                                            <Typography variant="caption" color="text.secondary">{t('mcp.availableToolsHelp', 'Select which discovered tools this agent may use.')}</Typography>
                                        </Box>
                                        {tools.length ? (
                                            <Button
                                                size="small"
                                                variant="text"
                                                disabled={disabled}
                                                onClick={() => {
                                                    const allToolNames = tools.map((tool) => tool.name);
                                                    const allSelected = allToolNames.every((name) => allowed.includes(name));
                                                    updateServer(selected.id, { allowedTools: allSelected ? [] : allToolNames });
                                                }}
                                                sx={{ flexShrink: 0, textTransform: 'none' }}
                                            >
                                                {tools.every((tool) => allowed.includes(tool.name))
                                                    ? t('mcp.clearAllTools', 'Clear all')
                                                    : t('mcp.selectAllTools', 'Select all')}
                                            </Button>
                                        ) : null}
                                    </Box>
                                    {tools.length ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                                            {tools.map((tool) => (
                                                <FormControlLabel
                                                    key={tool.name}
                                                    sx={{ m: 0, border: 1, borderColor: allowed.includes(tool.name) ? 'primary.main' : 'divider', borderRadius: 1, pr: 1 }}
                                                    control={<Checkbox size="small" checked={allowed.includes(tool.name)} onChange={(event) => updateServer(selected.id, { allowedTools: event.target.checked ? [...new Set([...allowed, tool.name])] : allowed.filter((name) => name !== tool.name) })} />}
                                                    label={<Typography variant="body2" fontWeight={600}>{tool.name}</Typography>}
                                                />
                                            ))}
                                        </Box>
                                    ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('mcp.noTools', 'Run a connection test to discover tools.')}</Typography>}
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => testServer(selected)}
                                        disabled={disabled || !canDiscover || testingId === selected.id}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {testingId === selected.id ? t('mcp.testing', 'Testing…') : t('mcp.test', 'Test connection')}
                                    </Button>
                                </Box>

                                {!canDiscover ? <Alert severity="info">{t('mcp.saveFirst', 'Save the agent before testing servers.')}</Alert> : null}
                                {status(selected) === 'connected' ? (
                                    <Alert icon={<CheckCircleOutlineIcon />} severity="success">{t('mcp.connectionSuccess', 'Connection test succeeded and tools were discovered.')}</Alert>
                                ) : null}
                                {status(selected) === 'failed' ? <Alert severity="error">{t('mcp.connectionFailed', 'Connection test failed. Check the URL, authentication, and backend logs.')}</Alert> : null}
                            </Box>
                        </Card>
                    ) : null}
                </Box>
            )}

            <Dialog open={Boolean(addDialog)} onClose={() => setAddDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t('mcp.addServer', 'Add MCP Server')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField label={t('mcp.serverName', 'Server name')} value={addDialog?.name || ''} onChange={(event) => setAddDialog((current) => ({ ...current, name: event.target.value }))} autoFocus required />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' } }}>
                        <Box sx={{ height: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: 1, borderColor: 'primary.main', borderRadius: { xs: '4px 4px 0 0', sm: '4px 0 0 4px' }, bgcolor: 'primary.50', color: 'primary.main', fontWeight: 700 }}>
                            <span>SSE / HTTP</span>
                            <Typography component="span" variant="caption" color="text.secondary" fontWeight={700}>{t('mcp.autoDetect', 'Auto-detect')}</Typography>
                        </Box>
                        <TextField label={t('mcp.serverUrl', 'Server URL')} value={addDialog?.url || ''} onChange={(event) => setAddDialog((current) => ({ ...current, url: event.target.value }))} placeholder="https://mcp.example.com/mcp" required sx={{ ml: { sm: '-1px' }, mt: { xs: '-1px', sm: 0 }, '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: { sm: 0 } } }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialog(null)}>{t('mcp.cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={addServer} disabled={!String(addDialog?.name || '').trim() || !String(addDialog?.url || '').trim()}>{t('mcp.addServer', 'Add MCP Server')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'error.50', color: 'error.main' }}>
                        <DeleteOutlineIcon />
                    </Box>
                    {t('mcp.deleteTitle', 'Delete MCP server?')}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('mcp.deleteMessage', 'Remove “{{name}}” from this agent?', { name: deleteTarget?.name || '' })}
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {t('mcp.deleteWarning', 'Its connection settings and tool permissions will be removed when you save the agent.')}
                    </Alert>
                    {deleteError ? <Alert severity="error" sx={{ mt: 1.5 }}>{deleteError}</Alert> : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDeleteTarget(null)} variant="outlined" disabled={deleting}>
                        {t('mcp.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={deleting}
                        onClick={async () => {
                            try {
                                setDeleting(true);
                                setDeleteError('');
                                if (canDiscover && onDelete) await onDelete(deleteTarget?.id);
                                onChange(items.filter((item) => item.id !== deleteTarget?.id));
                                setDeleteTarget(null);
                            } catch (error) {
                                console.error('Failed to delete MCP server', error);
                                setDeleteError(t('mcp.deleteError', 'Could not delete the MCP server. Please try again.'));
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? t('mcp.deleting', 'Deleting…') : t('mcp.deleteConfirm', 'Delete server')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default McpServersEditor;
